// Firestore data access. Every path and field mirrors the Flutter services so web and
// mobile read/write the very same documents.
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  runTransaction,
  serverTimestamp,
  increment,
  writeBatch,
  deleteField,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { dateKey, dateTimeKey, isoNowZ, reminderUtcSlot, utcTimestampKey } from './dates';
import { serializeProgram } from './program';
import { checkAndAwardBadges } from './badges';
import { requestXpSync } from './xp';

const randId = () => `${Date.now()}_${Math.floor(Math.random() * 999999)}`;
const bestEffort = (p) => Promise.resolve(p).catch(() => {});

// ───────────────────────── Users ─────────────────────────

export const DEFAULT_REMINDERS = { nutrition: 20 * 60, program: 9 * 60, water: 14 * 60 };

/** Fields of a brand-new user doc — same defaults as UserModel.toMap(). */
export function newUserDoc({ uid, email, firstName, lastName, role, exerciseProgramID, age = null }) {
  return {
    id: uid,
    email: email || '',
    firstName: firstName || 'User',
    lastName: lastName || '',
    age,
    height_cm: null,
    exerciseProgramID: exerciseProgramID || null,
    fitnessLevel: null,
    trainerEmail: null,
    role: role || 'trainee',
    subscription: 't0',
    subscriptionSource: null,
    notificationsEnabled: true,
    notificationsNutritionEnabled: true,
    notificationsProgramEnabled: true,
    notificationsWeeklySummaryEnabled: true,
    notificationsWaterEnabled: true,
    nutritionReminderMinute: DEFAULT_REMINDERS.nutrition,
    nutritionReminderUtcSlot: reminderUtcSlot(DEFAULT_REMINDERS.nutrition),
    programReminderMinute: DEFAULT_REMINDERS.program,
    programReminderUtcSlot: reminderUtcSlot(DEFAULT_REMINDERS.program),
    waterReminderMinute: DEFAULT_REMINDERS.water,
    waterReminderUtcSlot: reminderUtcSlot(DEFAULT_REMINDERS.water),
    programDurationInsteadOfSetsReps: true,
    syncExerciseAcrossDays: false,
    starterTodoCreateProgram: false,
    starterTodoCustomizeProgram: false,
    starterTodoLogFirstMeal: false,
    starterTodoAddStudent: false,
    starterTodoAddWidget: false,
    starterTodosEnabled: true,
    starterTodosPaywallShown: false,
    starterTodosWidgetPaywallShown: false,
  };
}

/** Normalizes a raw user doc with the same fallbacks as UserModel.fromMap. */
export function normalizeUser(id, d = {}) {
  const fallback = d.notificationsEnabled ?? true;
  const minute = (v, def) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0 || n >= 1440) return def;
    return n - (n % 15);
  };
  return {
    ...d,
    id,
    email: d.email || '',
    firstName: d.firstName || '',
    lastName: d.lastName || '',
    heightCm: d.height_cm ?? d.height ?? null,
    role: d.role || 'trainee',
    isTrainer: String(d.role || '').toLowerCase() === 'trainer',
    subscription: String(d.subscription || 't0').toLowerCase(),
    isPremium: ['t1', 'super', 'limitless'].includes(String(d.subscription || '').toLowerCase()),
    notificationsNutritionEnabled: d.notificationsNutritionEnabled ?? fallback,
    notificationsProgramEnabled: d.notificationsProgramEnabled ?? fallback,
    notificationsWeeklySummaryEnabled: d.notificationsWeeklySummaryEnabled ?? fallback,
    notificationsWaterEnabled: d.notificationsWaterEnabled ?? fallback,
    nutritionReminderMinute: minute(d.nutritionReminderMinute, DEFAULT_REMINDERS.nutrition),
    programReminderMinute: minute(d.programReminderMinute, DEFAULT_REMINDERS.program),
    waterReminderMinute: minute(d.waterReminderMinute, DEFAULT_REMINDERS.water),
    programDurationInsteadOfSetsReps: d.programDurationInsteadOfSetsReps ?? true,
    syncExerciseAcrossDays: d.syncExerciseAcrossDays ?? false,
    starterTodosEnabled: d.starterTodosEnabled ?? true,
    waterGoalMl: Number(d.waterGoalMl) > 0 ? Number(d.waterGoalMl) : 2500,
  };
}

export const userRef = (uid) => doc(db, 'users', uid);

export function updateUser(uid, data) {
  return updateDoc(userRef(uid), data);
}

export function markStarterTodoDone(uid, field) {
  return bestEffort(updateDoc(userRef(uid), { [field]: true }));
}

export function updateReminderMinute(uid, kind, minute) {
  return updateDoc(userRef(uid), {
    [`${kind}ReminderMinute`]: minute,
    [`${kind}ReminderUtcSlot`]: reminderUtcSlot(minute),
  });
}

/** Creates the Firestore user doc (+ empty placeholder program) for a new account. */
export async function createUserProfile({ uid, email, firstName, lastName, role = 'trainee', age = null }) {
  let exerciseProgramID = null;
  try {
    exerciseProgramID = await createProgram();
  } catch (e) {
    // The app also continues without a placeholder program.
  }
  await setDoc(userRef(uid), newUserDoc({ uid, email, firstName, lastName, role, exerciseProgramID, age }));
}

/** Archives users/{uid} to deletedUsers/{uid} and removes the user doc (AuthService.deleteAccount). */
export async function archiveAndDeleteUserDoc(uid) {
  const snap = await getDoc(userRef(uid));
  if (!snap.exists()) return;
  const data = { ...snap.data(), deletedAt: new Date().toISOString(), originalUid: uid };
  const batch = writeBatch(db);
  batch.set(doc(db, 'deletedUsers', uid), data);
  batch.delete(userRef(uid));
  await batch.commit();
}

export function updateMacroTargets(uid, targets) {
  return updateDoc(userRef(uid), { macro_targets: targets });
}

export async function mergeProfileAgeHeightIfMissing(uid, age, heightCm) {
  try {
    const snap = await getDoc(userRef(uid));
    if (!snap.exists()) return;
    const d = snap.data();
    const updates = {};
    if (d.age == null) updates.age = age;
    if (d.height_cm == null && d.height == null) updates.height_cm = heightCm;
    if (Object.keys(updates).length) await updateDoc(userRef(uid), updates);
  } catch (e) {
    // best-effort
  }
}

export async function recordNutritionEstimateToHistory(uid) {
  return bestEffort(updateDoc(userRef(uid), { [`nutritionEstimateHistory.${utcTimestampKey()}`]: true }));
}

export async function recordAiProgramToHistory(uid, program) {
  return bestEffort(updateDoc(userRef(uid), { [`programHistory.ai.${utcTimestampKey()}`]: serializeProgram(program) }));
}

// ───────────────────────── Programs ─────────────────────────

export const programRef = (id) => doc(db, 'exercisePrograms', id);

export async function createProgram() {
  const ref = await addDoc(collection(db, 'exercisePrograms'), { program: { monday: [] } });
  return ref.id;
}

export function saveProgram(programId, program) {
  return updateDoc(programRef(programId), { program: serializeProgram(program) });
}

/** Accepting an AI program: new program doc, assign it, mark the starter-todo. */
export async function assignNewProgram(uid, program) {
  const id = await createProgram();
  await saveProgram(id, program);
  await updateDoc(userRef(uid), { exerciseProgramID: id });
  await markStarterTodoDone(uid, 'starterTodoCreateProgram');
  return id;
}

// ───────────────────────── Program insights & history ─────────────────────────

export function insightsQuery(uid) {
  return query(collection(db, 'users', uid, 'program_insights'), orderBy('createdAt', 'desc'));
}

export function saveInsight(uid, programId, program, result) {
  const id = randId();
  return setDoc(doc(db, 'users', uid, 'program_insights', id), {
    programId: programId || null,
    program: program ? serializeProgram(program) : null,
    pros: result.pros,
    cons: result.cons,
    advices: result.advices,
    createdAt: serverTimestamp(),
  });
}

export function historyQuery(uid) {
  return query(collection(db, 'users', uid, 'program_history'), orderBy('created_at', 'desc'));
}

/** reason: 'manual_snapshot' (user-taken) or 'before_restore' (auto backup before a restore). */
export function saveProgramSnapshot(uid, program, reason = 'manual_snapshot') {
  const n = new Date();
  const p = (x, w = 2) => String(x).padStart(w, '0');
  const id = `${n.getFullYear()}${p(n.getMonth() + 1)}${p(n.getDate())}T${p(n.getHours())}${p(n.getMinutes())}${p(n.getSeconds())}${p(n.getMilliseconds(), 3)}_${Math.floor(Math.random() * 999999)}`;
  return setDoc(doc(db, 'users', uid, 'program_history', id), {
    reason,
    created_at: serverTimestamp(),
    created_by: 'user',
    program_snapshot: serializeProgram(program),
  });
}

/** Max snapshots a user may keep — keep in sync with ProgramHistoryService.maxSnapshotsFor. */
export function maxSnapshotsFor(subscription) {
  const sub = String(subscription || 't0').toLowerCase();
  if (sub === 'super' || sub === 'limitless') return 200;
  if (sub === 't1') return 50;
  return 10;
}

export function deleteProgramSnapshot(uid, snapshotId) {
  return deleteDoc(doc(db, 'users', uid, 'program_history', snapshotId));
}

/**
 * Replaces the current program with a snapshot. The current program is saved as a
 * 'before_restore' snapshot first so the restore can be undone (same as the app).
 */
export async function restoreProgramSnapshot(uid, programId, current, snapshot) {
  if (Object.values(current || {}).some((l) => l.length)) {
    await saveProgramSnapshot(uid, current, 'before_restore');
  }
  await saveProgram(programId, snapshot);
}

// ───────────────────────── Workout progress ─────────────────────────

export const workoutDayRef = (uid, d) => doc(db, 'progress_workouts', uid, 'days', dateKey(d));
export const workoutDaysCol = (uid) => collection(db, 'progress_workouts', uid, 'days');

export async function setExerciseCompleted({ uid, date, programId, exerciseId, completed, allExerciseIds }) {
  const ref = workoutDayRef(uid, date);
  let dayCompleted = false;
  let wasCompleted = false;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur = snap.data() || {};
    wasCompleted = cur.completed === true;
    const exercises = { ...(cur.exercises || {}) };
    exercises[exerciseId] = { ...(exercises[exerciseId] || {}), completed };
    const all = allExerciseIds.length === 0
      ? completed
      : allExerciseIds.every((id) => (id === exerciseId ? completed : exercises[id]?.completed === true));
    dayCompleted = all;
    tx.set(ref, { program_id: programId, completed: all, exercises }, { merge: true });
  });
  if (dayCompleted) {
    requestXpSync({ immediate: true });
    bestEffort(checkAndAwardBadges(uid));
  }
  // True only when this tap is the one that finished the day (drives the celebration).
  return dayCompleted && !wasCompleted;
}

// ───────────────────────── Body progress ─────────────────────────

export const bodyDaysCol = (uid) => collection(db, 'progress_body', uid, 'days');

export async function setBodyMetrics(uid, date, weightKg, bodyFatPercent, existingKey) {
  const data = {};
  if (weightKg != null) data.weight_kg = weightKg;
  if (bodyFatPercent != null) data.body_fat_percent = bodyFatPercent;
  if (!Object.keys(data).length) return;
  const key = existingKey || dateTimeKey(date);
  await setDoc(doc(db, 'progress_body', uid, 'days', key), data, { merge: !existingKey });
  bestEffort(checkAndAwardBadges(uid));
}

export function deleteBodyEntry(uid, key) {
  return deleteDoc(doc(db, 'progress_body', uid, 'days', key));
}

// ───────────────────────── Nutrition ─────────────────────────

export const nutritionDayRef = (uid, key) => doc(db, 'users', uid, 'nutrition_days', key);
export const nutritionDaysCol = (uid) => collection(db, 'users', uid, 'nutrition_days');

const ZERO = { calories_kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };

export function macrosOf(m) {
  return {
    calories_kcal: Math.round(Number(m?.calories_kcal) || 0),
    protein_g: Math.round(Number(m?.protein_g) || 0),
    carbs_g: Math.round(Number(m?.carbs_g) || 0),
    fat_g: Math.round(Number(m?.fat_g) || 0),
  };
}

function dayTotals(entries) {
  return entries.reduce((acc, e) => {
    const t = macrosOf(e.totals);
    return {
      calories_kcal: acc.calories_kcal + t.calories_kcal,
      protein_g: acc.protein_g + t.protein_g,
      carbs_g: acc.carbs_g + t.carbs_g,
      fat_g: acc.fat_g + t.fat_g,
    };
  }, { ...ZERO });
}

async function mutateDay(uid, date, fn) {
  const key = dateKey(date);
  const ref = nutritionDayRef(uid, key);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const entries = [...(snap.data()?.entries || [])];
    const next = fn(entries);
    if (!next) return;
    tx.set(ref, { date: key, entries: next, day_totals: dayTotals(next), updated_at: serverTimestamp() }, { merge: true });
  });
}

async function afterMealLogged(uid) {
  try {
    const snap = await getDoc(userRef(uid));
    if (snap.data()?.starterTodoLogFirstMeal !== true) await updateDoc(userRef(uid), { starterTodoLogFirstMeal: true });
  } catch (e) {
    // best-effort
  }
  bestEffort(checkAndAwardBadges(uid));
  // Last, right before the save resolves: the caller's meal celebration then
  // queues ahead of the XP this pays.
  requestXpSync({ immediate: true });
}

export async function addAiEntry(uid, date, estimate) {
  const now = isoNowZ();
  const entry = {
    id: randId(),
    created_at: now,
    meal_type: estimate.mealType,
    source: 'ai',
    items: estimate.items.map((i) => ({
      id: `item_${randId()}`,
      food_name: i.foodName,
      quantity: i.quantity,
      quantity_estimated: i.quantityEstimated,
      macros: { calories_kcal: i.caloriesKcal, protein_g: i.proteinG, carbs_g: i.carbsG, fat_g: i.fatG },
      ...(i.assumptionNote ? { assumption_note: i.assumptionNote } : {}),
      confidence: 0.8,
      source: 'ai',
      created_at: now,
    })),
    totals: {
      calories_kcal: estimate.mealTotals.caloriesKcal,
      protein_g: estimate.mealTotals.proteinG,
      carbs_g: estimate.mealTotals.carbsG,
      fat_g: estimate.mealTotals.fatG,
    },
  };
  await mutateDay(uid, date, (entries) => [...entries, entry]);
  await afterMealLogged(uid);
}

export async function addQuickAddEntry(uid, date, { label, caloriesKcal, proteinG, carbsG, fatG }) {
  const entry = {
    id: randId(),
    created_at: isoNowZ(),
    totals: { calories_kcal: caloriesKcal, protein_g: proteinG, carbs_g: carbsG, fat_g: fatG },
    type: 'quick_add',
    label,
  };
  await mutateDay(uid, date, (entries) => [...entries, entry]);
  await afterMealLogged(uid);
}

export function deleteNutritionEntry(uid, date, entryId) {
  return mutateDay(uid, date, (entries) => {
    const next = entries.filter((e) => e.id !== entryId);
    return next.length === entries.length ? null : next;
  });
}

export function restoreNutritionEntry(uid, date, entry) {
  return mutateDay(uid, date, (entries) => {
    if (entries.some((e) => e.id === entry.id)) return null;
    const at = entries.findIndex((e) => String(e.created_at || '') > String(entry.created_at || ''));
    const next = [...entries];
    next.splice(at < 0 ? next.length : at, 0, entry);
    return next;
  });
}

export const isAiEntry = (e) => !!e.meal_type && Array.isArray(e.items) && e.items.length > 0;

// ───────────────────────── Water ─────────────────────────

export const WATER = { defaultGoal: 2500, min: 1000, max: 5000, step: 250, quick: [200, 330, 500] };
export const waterDayRef = (uid, key) => doc(db, 'users', uid, 'water_days', key);
export const waterDaysCol = (uid) => collection(db, 'users', uid, 'water_days');

export async function addWaterMl(uid, ml, date = new Date()) {
  const key = dateKey(date);
  await setDoc(waterDayRef(uid, key), { date: key, ml: increment(ml), updated_at: serverTimestamp() }, { merge: true });
  // May reach the water goal.
  if (ml > 0) requestXpSync();
}

export function setWaterGoal(uid, goalMl) {
  return updateDoc(userRef(uid), { waterGoalMl: goalMl });
}

export function formatLiters(ml, lang) {
  const fixed = (ml / 1000).toFixed(2).replace(/\.?0+$/, '');
  return `${lang === 'tr' ? fixed.replace('.', ',') : fixed} L`;
}

// ───────────────────────── Weekly summary ─────────────────────────

export const weeklySummaryRef = (uid, weekId) => doc(db, 'users', uid, 'weekly_summaries', weekId);

export function saveWeeklySummary(uid, weekId, s) {
  return setDoc(weeklySummaryRef(uid, weekId), {
    weekStart: s.weekStart,
    weekEnd: s.weekEnd,
    language: s.language,
    headline: s.headline,
    programSummary: s.programSummary,
    nutritionSummary: s.nutritionSummary,
    tips: s.tips,
    stats: s.stats,
    unlockedByAd: false,
    createdAt: serverTimestamp(),
  });
}

// ───────────────────────── Trainer ↔ trainee ─────────────────────────

export async function generateTrainerCode(trainerEmail, subscription) {
  if (String(subscription).toLowerCase() === 't0') {
    try {
      const active = await getDocs(query(
        collection(db, 'codes'),
        where('trainer', '==', trainerEmail),
        where('expiration', '>', Timestamp.fromDate(new Date())),
      ));
      if (active.size >= 3) throw new Error('MAXIMUM_CODE_LIMIT_REACHED');
    } catch (e) {
      if (e.message === 'MAXIMUM_CODE_LIMIT_REACHED') throw e;
    }
  }
  let code = '';
  for (let i = 0; i < 100; i += 1) {
    code = String(1000 + Math.floor(Math.random() * 9000));
    // eslint-disable-next-line no-await-in-loop
    const existing = await getDoc(doc(db, 'codes', code));
    if (!existing.exists()) break;
    code = '';
  }
  if (!code) throw new Error('Unable to generate a unique code. Please try again.');
  await setDoc(doc(db, 'codes', code), {
    code,
    expiration: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 3600 * 1000)),
    trainer: trainerEmail,
  });
  return code;
}

export function trainerCodesQuery(trainerEmail) {
  return query(collection(db, 'codes'), where('trainer', '==', trainerEmail));
}

export function deleteTrainerCode(code) {
  return deleteDoc(doc(db, 'codes', code));
}

export async function getTrainerEmailFromCode(code) {
  const snap = await getDoc(doc(db, 'codes', code));
  if (!snap.exists()) return null;
  const d = snap.data();
  const exp = d.expiration?.toDate?.();
  if (exp && new Date() > exp) return null;
  return d.trainer || null;
}

async function findTrainer(trainerEmail) {
  const snap = await getDocs(query(collection(db, 'users'), where('email', '==', trainerEmail), where('role', '==', 'trainer'), limit(1)));
  return snap.docs[0] || null;
}

export async function connectTraineeToTrainer(traineeId, trainerEmail, usedCode) {
  await updateDoc(userRef(traineeId), { trainerEmail, starterTodoAddStudent: true });
  const trainer = await findTrainer(trainerEmail).catch(() => null);
  if (trainer) {
    const d = trainer.data();
    const list = Array.isArray(d.traineeList) ? [...d.traineeList] : [];
    if (!list.includes(traineeId)) {
      if (String(d.subscription || 't0').toLowerCase() === 't0' && list.length >= 3) throw new Error('MAXIMUM_TRAINEE_LIMIT_REACHED');
      list.push(traineeId);
      await updateDoc(trainer.ref, { traineeList: list, starterTodoAddStudent: true });
    }
  }
  if (usedCode) bestEffort(deleteTrainerCode(usedCode));
}

export async function disconnectTraineeFromTrainer(traineeId, trainerEmail) {
  await updateDoc(userRef(traineeId), { trainerEmail: deleteField() });
  const trainer = await findTrainer(trainerEmail).catch(() => null);
  if (trainer) {
    const list = (trainer.data().traineeList || []).filter((x) => x !== traineeId);
    await updateDoc(trainer.ref, { traineeList: list });
  }
}

export async function removeTraineeFromList(trainerUid, traineeList, traineeId) {
  await updateDoc(userRef(trainerUid), { traineeList: traineeList.filter((x) => x !== traineeId) });
}

export async function loadTrainees(traineeIds) {
  const out = [];
  for (const id of traineeIds) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const snap = await getDoc(userRef(id));
      if (snap.exists()) out.push({ id: snap.id, ...snap.data() });
      else out.push({ id, missing: true });
    } catch (e) {
      out.push({ id, restricted: true });
    }
  }
  return out;
}

// ───────────────────────── Feedback ─────────────────────────

export function sendFeedback(uid, type, message) {
  return addDoc(collection(db, 'feedback'), {
    userId: uid || null,
    type,
    message: message.trim(),
    status: 'new',
    createdAt: serverTimestamp(),
    appVersion: 'web',
    platform: 'web',
    osVersion: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    deviceModel: 'browser',
  });
}

export { orderBy, query, collection, doc, limit };
